<template>
  <div v-if="localeStore?.isUsLocale" class="dynamic-page-wrapper">
    <div class="main-content desk-hor-mob-col">
      <div class="product-images-wrapper desk-hor-mob-col">
        <CloudinaryImage
          :public-id="'zpokgnfxdmfhvffua0yq'"
          :options="imgSizes"
          :alt="'Solo Stove Digital Gift Card'"
          :image-classes-override="['product-image']"
        />
      </div>
      <form class="gift-card-form" @submit.prevent="giftCardAtc">
        <h1 class="product-title h3">Solo Stove Digital Gift Card</h1>
        <div class="input-container amount">
          <label class="input-label">Select An Amount:</label>
          <div class="options-wrapper flex-inline flex-wrap">
            <div
              v-for="option in amountOptions"
              :key="option.amountLabel"
              :value="option.value"
              class="option"
              role="button"
              :aria-label="`Set Gift Card amount to ${option.amountLabel}`"
              @click.prevent="setSelectedAmount(option.value)"
            >
              <div
                :class="[
                  'amount-option-wrapper',
                  { selected: option.value === state.selectedAmount && !state.customAmount },
                ]"
              >
                <div class="option pointer">
                  {{ option.amountLabel }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="input-container">
          <label class="input-label">Custom Amount:</label>
          <div class="input-field">
            <Input
              ref="customAmountField"
              variant="text"
              name="Custom Amount"
              type="number"
              min="25"
              max="1000"
              placeholder="($25 - $1,000)"
              label="Custom Amount"
              emit-input="text-input"
              @text-input="state.customAmount = $event.value"
            />
          </div>
          <div v-if="!isCustomAmountValid" class="error-msg">You must enter an amount between $25 - $1,000</div>
        </div>
        <div class="input-container">
          <label class="input-label">To:</label>
          <div class="input-field">
            <Input
              variant="text"
              aria-autocomplete="both"
              autocomplete="current-name"
              name="recipient Name"
              type="text"
              placeholder="Recipient Name"
              label="recipient Name"
              :required="true"
              emit-input="text-input"
              @text-input="state.recipientName = $event.value"
            />
          </div>
          <div class="input-field">
            <Input
              variant="email"
              aria-autocomplete="both"
              autocomplete="current-email"
              name="recipient Email"
              type="email"
              :placeholder="`Recipient ${useTranslate('GLOBALS.emailAddress')}`"
              :label="`Recipient ${useTranslate('GLOBALS.emailAddress')}`"
              :required="true"
              :not-valid-override="!state.recipientEmailValid && !!state.recipientEmail"
              emit-input="text-input"
              @text-input="
                (event: any) => {
                  state.recipientEmail = event.value;
                  state.recipientEmailValid = isEmailValid(state.recipientEmail);
                }
              "
            >
              <template #errorMessage>
                {{ !state.recipientEmailValid ? 'Invalid Email Format' : '' }}
              </template>
            </Input>
          </div>
        </div>
        <div class="input-container">
          <label class="input-label">From:</label>
          <div class="input-field">
            <Input
              variant="text"
              aria-autocomplete="both"
              autocomplete="current-name"
              name="Your Name"
              type="text"
              placeholder="Your Name"
              label="Your Name"
              :required="true"
              emit-input="text-input"
              @text-input="state.senderName = $event.value"
            />
          </div>
          <div class="input-field">
            <Input
              variant="email"
              aria-autocomplete="both"
              autocomplete="current-email"
              name="Your Email"
              type="email"
              :placeholder="`Your ${useTranslate('GLOBALS.emailAddress')}`"
              :label="`Your ${useTranslate('GLOBALS.emailAddress')}`"
              :required="true"
              :not-valid-override="!state.senderEmailValid && !!state.senderEmail"
              emit-input="text-input"
              @text-input="
                (event: any) => {
                  state.senderEmail = event.value;
                  state.senderEmailValid = isEmailValid(state.senderEmail);
                }
              "
            >
              <template #errorMessage>
                {{ !state.senderEmailValid ? 'Invalid Email Format' : '' }}
              </template>
            </Input>
          </div>
        </div>
        <div class="input-container">
          <label class="input-label">Gift Message:</label>
          <div class="input-field">
            <Input
              variant="textarea"
              name="Gift Message"
              type="text"
              maxlength="240"
              placeholder="Message (Optional)"
              label="Gift Message"
              emit-input="text-input"
              @text-input="state.giftMsg = $event.value"
            />
          </div>
        </div>
        <div class="button-container">
          <AddToCartButton
            ref="giftAtc"
            btn-type="submit"
            :disable-btn="!isCustomAmountValid || a2cDisabled"
            :product="cartProduct"
            :images="cartImage"
            render-mode="gift-card"
          />
        </div>
        <h5>Gift Card Details</h5>
        <ul>
          <li>There's no expiration date for gift cards.</li>
          <li>Gift cards are nonrefundable and are available in USD only.</li>
        </ul>
      </form>
    </div>
  </div>
  <div v-else class="dynamic-page-wrapper">
    <div class="main-content">
      <h1>Not Available in Your Locale</h1>
    </div>
  </div>
</template>

<script setup lang="ts">
import { metaGen } from '~/util/metadata';
import { emailValid } from '~/util/string';
import { useRootStore } from '~/store';
import { useLocaleStore } from '~/store/locale';
import { useDeviceStore } from '~/store/device';
// import { useCloudinary } from '~/composables/cloudinary';

const deviceStore = useDeviceStore();
const rootStore = useRootStore();
const localeStore = useLocaleStore();
// const cloudinary = useCloudinary();
// const localePath = useLocalePath();
const customAmountField = ref<any>(null);
const giftAtc = ref<any>(null);
useHead({
  ...(metaGen(rootStore?.brandSettings, {
    page_title: 'Digital Gift Cards and Certificates',
    page_description: 'Purchase a digital gift card as a perfect holiday or birthday present',
  }) as any),
});
const amountOptions = [
  { amountLabel: '$100', value: 100 },
  { amountLabel: '$250', value: 250 },
  { amountLabel: '$500', value: 500 },
];
const cartImage = [
  {
    file: {
      url: 'https://cdn11.bigcommerce.com/s-b5b4c/images/stencil/original/image-manager/solo-stove-gift-card.png?t=1663944421&_gl=1*3ubfr9*_ga*MTgwODMwMjM4NC4xNjM0ODMxNDcx*_ga_WS2VZYPC6G*MTY2Mzk0NDM1MS4xNjYuMS4xNjYzOTQ1MTA5LjMzLjAuMA..',
    },
  },
];

const state = reactive({
  selectedAmount: 250,
  customAmount: null as number | string | null,
  recipientName: '',
  recipientEmail: '',
  recipientEmailValid: false,
  senderEmailValid: false,
  senderName: null as string | null,
  senderEmail: '',
  giftMsg: '',
});

const a2cDisabled = computed(() => {
  return !(
    state.senderName &&
    state.senderEmail &&
    state.recipientName &&
    state.recipientEmail &&
    state.senderEmailValid &&
    state.recipientEmailValid
  );
});
const cartProduct = computed(() => {
  return {
    gift_certificates: [
      {
        name: 'Solo Stove Digital Gift Card',
        theme: 'General',
        amount: state.customAmount ? state.customAmount : state.selectedAmount,
        quantity: 1,
        sender: {
          name: state.senderName,
          email: state.senderEmail,
        },
        recipient: {
          name: state.recipientName,
          email: state.recipientEmail,
        },
        message: state.giftMsg,
      },
    ],
  };
});
const isCustomAmountValid = computed(() => {
  if (
    (state.customAmount !== null || state.customAmount !== '') &&
    typeof state.customAmount === 'string' &&
    parseInt(state.customAmount) >= 0
  ) {
    const parsed = parseInt(state.customAmount);
    return parsed >= 25 && parsed <= 1000;
  }
  return true;
});

const imgSizes = computed<ImageOptions>(() => {
  return {
    width: deviceStore.isMobile ? 390 : deviceStore.isDesktop ? 622 : 768,
    height: deviceStore.isMobile ? 338 : deviceStore.isDesktop ? 672 : 410,
    transform: 'pad',
    // gravity: 'center',
    quality: 'auto:eco',
  };
});

function setSelectedAmount(value: number) {
  state.customAmount = null;
  customAmountField.value?.clearInput();
  state.selectedAmount = value;
}
function isEmailValid(email: string) {
  const isValid = emailValid(email);
  return isValid;
}
async function giftCardAtc() {
  await giftAtc.value?.addToCartAction(null, true);
}
</script>

<style lang="scss" scoped>
.product-title {
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 125%;
  text-transform: capitalize;
  @include local-mixins.tablet_and_mobile {
    font-size: 26px;
    margin-top: 1.5rem;
  }
}
.gift-card-form {
  color: $color-neutral-cool-900;

  .input-container {
    &.amount {
      margin-top: 24px;
    }

    margin-top: 19px;
    .input-label {
      font-style: normal;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 150%;
    }
    .input-field {
      margin-top: 8px;
    }
  }
  .button-container {
    margin-top: 1.5rem;
  }
  h5 {
    font-style: normal;
    font-weight: 500;
    font-size: 1.25rem;
    line-height: 125%;
    margin-top: 2rem;
  }
  ul {
    list-style-type: disc;
    margin-left: 1rem;
  }
  li {
    font-size: 0.875rem;
    line-height: 150%;
  }
}
.main-content {
  justify-content: space-evenly;
}

.options-wrapper {
  gap: 8px;
}

.amount-option-wrapper {
  margin-top: 0.5rem;
  .option {
    @include local-mixins.ff-text-65;
    padding: 16px 45.5px;
    font-size: 1rem;
    @include local-mixins.tablet_and_mobile {
      padding: 16px 33.3px;
    }
  }
  &:not(.selected) {
    border: 2px solid $color-neutral-cool-900;
    border-radius: 4px;
  }
  &:deep() .select-expanded {
    min-width: #{local-functions.rem-calc(250)};
  }
  &.selected {
    background-color: $color-secondary-50;
    border: $color-secondary-500 2px solid;
    border-radius: 4px;
    color: $color-secondary-600;
  }
}

.product-images-wrapper {
  height: #{local-functions.rem-calc(672)};
  width: #{local-functions.rem-calc(622)};
  border-radius: 8px;
  background-color: $color-neutral-cool-50;
  @include local-mixins.tablet {
    height: 40vh;
    width: 100%;
    border-radius: 0;
  }
  @include local-mixins.tablet_and_mobile {
    height: 40vh;
    width: 100%;
    border-radius: 0;
  }
  :deep(.product-image) {
    object-fit: contain;
    height: 100%;
    width: 100%;
    padding: 0 10%;
    @include local-mixins.tablet {
      padding: 5% 10%;
    }
    @include local-mixins.tablet_and_mobile {
      width: 100%;
    }
  }
  :deep(.cloudinary-image-container) {
    height: 100%;
  }
}
.error-msg {
  font-weight: 400;
  line-height: 150%;
  color: #7d1c1c;
  margin-top: 0.5rem;
}
</style>
